import {
  Coverage,
  Driver,
  getInsuranceTypeDisplayName,
  InsurancePolicyCoverageTypes,
  ISitecoreReviewSummaryItem,
  VehicleCoverage,
} from '@/interfaces';
import { formatCurrency } from '@/utils/text-transforms';
import { CarService as _carService } from '@/services/store/car.service';
import {
  getAdditionalText,
  getPdsOrSpdsVersion,
  InsuranceDocumentType,
  policyDetailsCheckIcon,
} from './PolicyDetailsHelperMapper';
import { getFormInput } from '@/utils/form-input-helper';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { SelfServiceSessionPages } from '@/enums/self-service-session-pages';
import { SelfServiceService } from '@/services/store/self-service-service';

export enum MotorAdditionalBenefits {
  Windscreen = 'Windscreen',
  FreeHireCar = 'FreeHireCar',
  Rating1Protection = 'Rating1Protection',
}

/**
 * Returns the populated SummaryItems with DOM ready text.
 * @param coverage The Motor insurance coverage
 * @param content The props.content for the page
 */
export async function mapAsMotorCoverage(
  coverage: VehicleCoverage,
  content: any,
  dictionary: Record<string, string>
): Promise<any> {
  const summary = {} as any;
  // Policy details
  summary.insuranceTypeDisplayName = getInsuranceTypeDisplayName(dictionary, coverage.coverageCode);
  summary.vehicleDetails = getVehicleDetails(coverage);
  summary.registrationNumber = getVehicleRegistrationNumber(coverage);
  summary.vehicleLocation = getVehicleLocation(coverage);
  summary.vehicleUse = coverage.isVehicleUsePrivate ? 'Private' : 'Business';
  summary.finance = getFinance(coverage);
  summary.listedDrivers = getListedDrivers(coverage.drivers).join('<br/>');
  summary.sumInsured = getSumInsured(coverage);

  // Applicable excesses
  if (isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar(coverage)) {
    // Only add 'Applicable excesses' and 'Optional benefits' if the policy is for a motor vehicle (NOT motorcycle, caravan, trailer, etc.)
    summary.basicExcess = await getBasicExcess(coverage);
    summary.highPerformanceExcess = getHighPerformanceExcess(coverage);
    summary.listedDriverAgeExcess = getDriverExcess(content, 'listedDriverAgeExcessValue', 'listedDriverAgeExcessText');
    summary.unlistedDriverAgeExcess = getDriverExcess(
      content,
      'unlistedDriverAgeExcessValue',
      'unlistedDriverAgeExcessText'
    );
    summary.inexperiencedDriverExcess = getDriverExcess(
      content,
      'inexperiencedDriverExcessValue',
      'inexperiencedDriverExcessText'
    );

    // Optional benefits
    summary.additionalHireCar = getAdditionalHireCar(coverage, content);
    summary.excessFreeGlassRepair = getExcessFreeGlassRepair(coverage, content);
    summary.noClaimDiscountProtection = getNoClaimDiscountProtection(coverage, content);
  }

  // Insurance documents
  summary.pdsVersion = await getPdsOrSpdsVersion(coverage, InsuranceDocumentType.PDS);
  summary.spdsVersion = await getPdsOrSpdsVersion(coverage, InsuranceDocumentType.SPDS);

  // Your discounts is handled by a slot in ReviewSummary
  //summary.yourDiscounts = getYourDiscounts(coverage);

  return summary;
}

function getFinance(coverage: VehicleCoverage): string | null {
  if (coverage.finance && coverage.finance.length > 0) {
    return coverage.finance;
  }
  return null;
}

function getSumInsured(coverage: VehicleCoverage) {
  if (coverage.isMarketValue) {
    return 'Market value';
  }
  if (coverage.sumInsured) {
    return formatCurrency(coverage.sumInsured, 2);
  }
  if (coverage.agreedValue) {
    return formatCurrency(coverage.agreedValue, 2);
  }
  return null;
}

function getVehicleDetails(coverage: VehicleCoverage) {
  if (coverage.isMotor) {
    return `${coverage.vehicle.formattedLine1}<br/>${coverage.vehicle.formattedLine2}`;
  }

  const coverageType = coverage?.coverageType?.toString();

  const isMotorcycle = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Motorcycle];
  const isOther =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorComprehensiveOther] ||
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorThirdPartyLiabilityOther] ||
    coverageType ===
      InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorFireTheftAndThirdPartyLiabilityOther];
  const isBoat =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.BoatComprehensive] ||
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.BoatThirdPartyLiability];
  const isCaravan = coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.Caravan];

  if (isMotorcycle || isOther || isBoat || isCaravan) {
    return coverage.vehicle.formattedLine1;
  }

  // default return formattedSummary
  return coverage.vehicle.formattedSummary;
}

function getVehicleRegistrationNumber(coverage: VehicleCoverage) {
  // default return formattedSummary
  return coverage.vehicle.registrationNumber;
}

async function getBasicExcess(coverage: VehicleCoverage): Promise<string | null> {
  try {
    const response = await _carService.getMotorExcess(coverage.coverageCode);
    if (response?.length > 0) {
      const result = response.find((x: any) => x.code === coverage.basicExcessCode)?.name;
      return formatCurrency(result) || null;
    }
    return null;
  } catch (error) {
    console.error('err', error);
    return null;
  }
}

function getDriverExcess(content: any, excessValueFormInputId: string, excessTextFormInputId: string): string | null {
  // Retrieve Sitecore form input values
  const driverExcessValue = getFormInput(content, excessValueFormInputId)?.label?.value?.replaceAll('"', "'") ?? '';
  const driverExcessAdditionalText = getAdditionalText(content, excessTextFormInputId);

  if (!driverExcessValue || !driverExcessAdditionalText) {
    return null;
  }

  let result: string | null = null;

  // Format items for currency and update bookmarks
  const formattedDriverExcessValue = formatCurrency(driverExcessValue);
  result = formattedDriverExcessValue;
  result += replaceBookmark(excessValueFormInputId, driverExcessAdditionalText, formattedDriverExcessValue);

  return result;
}

function getHighPerformanceExcess(coverage: VehicleCoverage): string | null {
  if (coverage.highPerformanceExcess !== null && coverage.highPerformanceExcess > 0) {
    return formatCurrency(coverage.highPerformanceExcess);
  }
  return null;
}

function getAdditionalHireCar(coverage: VehicleCoverage, content: any): string | null {
  const hasHireCar = coverage.benefits
    .map((item) => item.toLowerCase())
    .includes(MotorAdditionalBenefits.FreeHireCar.toLowerCase());

  if (hasHireCar) {
    return policyDetailsCheckIcon + 'Added' + getAdditionalText(content, 'optionalBenefitsAdditionalHireCarText');
  }
  return null;
}

function getExcessFreeGlassRepair(coverage: VehicleCoverage, content: any): string | null {
  const hasWindscreen = coverage.benefits
    .map((item) => item.toLowerCase())
    .includes(MotorAdditionalBenefits.Windscreen.toLowerCase());

  if (hasWindscreen) {
    return policyDetailsCheckIcon + 'Added' + getAdditionalText(content, 'optionalBenefitsExcessFreeGlassRepairText');
  }
  return null;
}

function getNoClaimDiscountProtection(coverage: VehicleCoverage, content: any): string | null {
  const hasWindscreen = coverage.benefits
    .map((item) => item.toLowerCase())
    .includes(MotorAdditionalBenefits.Rating1Protection.toLowerCase());

  if (hasWindscreen) {
    return (
      policyDetailsCheckIcon + 'Added' + getAdditionalText(content, 'optionalBenefitNoClaimDiscountProtectionText')
    );
  }
  return null;
}

function getListedDrivers(listedDrivers: Driver[]): string[] {
  const drivers = [];
  for (const driver of listedDrivers) {
    drivers.push(
      `${driver.firstName} ${driver.lastName}${driver.isPrimary && listedDrivers.length > 1 ? ' (Primary)' : ''}`
    );
  }
  return drivers;
}

function getVehicleLocation(coverage: VehicleCoverage) {
  return `${coverage.garagingLocation}<br/>Kept when not in use: ${coverage.garagingType}`;
}

export function isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar(
  coverage: VehicleCoverage | Coverage
): boolean {
  const coverageType = coverage?.coverageType?.toString();
  const isComprehensiveCar =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorComprehensive];
  const isFireTheftAndThirdPartyCar =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorFireTheftAndThirdPartyLiability];
  const isThirdPartyPropertyDamageCar =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorThirdPartyLiability];
  return isComprehensiveCar || isFireTheftAndThirdPartyCar || isThirdPartyPropertyDamageCar;
}

/**
 * Returns whether the specified coverage can be upgraded to Comprehensive.
 */
export function isUpgradeable(coverage: VehicleCoverage): boolean {
  const coverageType = coverage?.coverageType?.toString();
  const isFireTheftAndThirdPartyCar =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorFireTheftAndThirdPartyLiability];
  const isThirdPartyPropertyDamageCar =
    coverageType === InsurancePolicyCoverageTypes[InsurancePolicyCoverageTypes.MotorThirdPartyLiability];

  return isFireTheftAndThirdPartyCar || isThirdPartyPropertyDamageCar;
}

/**
 * Updates the specified bookmark with the relevant quoteSessionId for the specified SelfService Page
 */
export async function updateQuoteSessionId(
  policyNumber: string,
  queryString: string,
  bookmark: string,
  selfServiceSessionPage: SelfServiceSessionPages,
  coverageId?: string
): Promise<string> {
  try {
    // Get a quoteSessionId for policyNumber and specific SelfService page
    const response = await SelfServiceService.createQuoteSession(policyNumber, selfServiceSessionPage, coverageId);
    if (response?.success) {
      // Update the querystring with the quoteSessionId
      const updatedQueryString = replaceBookmark(bookmark, queryString, response.data);
      return updatedQueryString;
    }
  } catch (error) {
    console.error('err', error);
  }

  return queryString;
}

export async function updatePrimaryLinkQueryString(
  policyNumber: string,
  item: ISitecoreReviewSummaryItem,
  coverage?: Coverage
): Promise<string> {
  const queryString = item.fields.primaryLink.value.querystring!;
  console.debug(queryString, item.displayName);
  if (queryString) {
    if (item.displayName === 'Motor-PolicyDetails-VehicleDetails') {
      return await updateQuoteSessionId(
        policyNumber,
        queryString,
        'changeVehicleSessionId',
        SelfServiceSessionPages.ChangeVehiclePage,
        coverage?.id
      );
    }
    if (item.displayName === 'Motor-PolicyDetails-VehicleLocation') {
      return await updateQuoteSessionId(
        policyNumber,
        queryString,
        'changeVehicleLocationSessionId',
        SelfServiceSessionPages.ChangeVehicleLocationPage,
        coverage?.id
      );
    }
  }

  return queryString;
}
